// drift-test:no-test-needed: UI page that renders state from typed API responses (openapi-generated). No business logic in the component; CLIVersionRow is a pure data->markup function. Backend logic is covered by api/admin_cli_versions_test.go + runner/cli_versions_test.go in this commit.
import { useState, useEffect, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import { useUser } from "../auth/useUser";
import { Header } from "../components/Header";
import { BrowserAlertsSetting } from "../components/BrowserAlertsSetting";
import { pushToast } from "../components/Toast";
import type { components } from "../api/types";

type AdminEngineSetting = components["schemas"]["AdminEngineSetting"];
type CLIVersionsResponse = components["schemas"]["CLIVersionsResponse"];
type CLIVersionStatusPair = components["schemas"]["CLIVersionStatusPair"];
type EngineAuthCapability = components["schemas"]["EngineAuthCapability"];
type EngineAuthStartResponse = components["schemas"]["EngineAuthStartResponse"];
type EngineAuthStatusResponse = components["schemas"]["EngineAuthStatusResponse"];
type EngineUsage = components["schemas"]["EngineUsage"];
type EngineUsageWindow = components["schemas"]["EngineUsageWindow"];
type EngineId = "codex" | "claude";

// Settings page — system-wide runtime knobs admins can flip without redeploy.
//
// Current shape: a single "Default engine" selector that overrides
// cfg.DefaultEngine. The orchestrator reads /admin_settings/runtime at the
// start of every Run, so the flip takes effect on the very next submission.
//
// When source="cfg_default" (no admin override set yet), the dropdown shows
// the orchestrator default selected but doesn't write a value until the
// admin clicks Save.
export function Settings() {
  const { data: me } = useUser();
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ["admin", "settings", "engine"],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/admin/settings/engine", {
        params: { header: { "X-Requested-With": "xhr" } },
      });
      if (error) throw error;
      return data as AdminEngineSetting;
    },
  });

  const versionsQ = useQuery({
    queryKey: ["admin", "cli-versions"],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/admin/cli-versions", {
        params: { header: { "X-Requested-With": "xhr" } },
      });
      if (error) throw error;
      return data as CLIVersionsResponse;
    },
    // Re-fetch every 5 min while the page is open — npm releases are
    // infrequent so polling more aggressively wastes admin → orchestrator
    // → npm registry round-trips. 5 min < server-side 15-min cache TTL
    // anyway; the second-fetch within a TTL window is served from cache.
    refetchInterval: 5 * 60 * 1000,
  });

  // 0176: the commit each part runs. This app's own is stamped at build time
  // (VITE_BUILD_COMMIT, "dev" for a local build); the service's is what its
  // public /health serves.
  const healthQ = useQuery({
    queryKey: ["admin", "health"],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/health");
      if (error) throw error;
      return data;
    },
    staleTime: 60_000,
  });

  const current = q.data;
  // Local UI state for the unsaved selection. When the server doc is unset
  // (source=cfg_default), default the UI to "codex" — matches the most
  // common cfg.DefaultEngine in production.
  const [selected, setSelected] = useState<"codex" | "claude" | null>(null);
  const activeChoice: "codex" | "claude" =
    selected ??
    (current?.default_engine === "claude" ? "claude" : "codex");

  const mut = useMutation({
    mutationFn: async (engine: "codex" | "claude") => {
      const { data, error } = await apiClient.PUT("/api/v1/admin/settings/engine", {
        params: { header: { "X-Requested-With": "xhr" } },
        body: { engine },
      });
      if (error) throw error;
      return data as AdminEngineSetting;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["admin", "settings", "engine"], data);
      pushToast(`Engine override set to ${data.default_engine}`);
      setSelected(null);
    },
    onError: (err) => {
      pushToast(`Failed: ${(err as Error).message}`, "error");
    },
  });

  if (!me) return null;

  const isOverride = current?.source === "admin_override";
  const dirty = selected !== null && selected !== current?.default_engine;
  const servedCommit = healthQ.data?.commit;
  // isPending, not isLoading: a read paused offline is pending but not
  // fetching, and must not fall through to the pre-stamp line.
  const serviceCommit = healthQ.isPending
    ? "loading…"
    : healthQ.error
      ? "unreachable"
      : typeof servedCommit === "string" && servedCommit !== ""
        ? servedCommit
        : "not reported (pre-stamp build)";

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={me} />
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-8 py-12">
        <h1 className="text-display font-medium text-ink-50 tracking-tightest mb-2">
          Settings
        </h1>
        <p className="text-sm text-ink-300 mb-8">
          System-wide runtime knobs. Changes take effect on the next run with
          no orchestrator restart.
        </p>

        <section className="rounded-md border border-ink-800 bg-ink-900/40 p-6">
          <div className="flex items-baseline justify-between gap-4 mb-4">
            <div>
              <h2 className="text-base font-medium text-ink-50">Default engine</h2>
              <p className="text-xs text-ink-400 mt-1">
                Which LLM adapter the orchestrator dispatches to when a new
                run is submitted. Admin override persists at{" "}
                <code className="text-ink-200">/admin_settings/runtime</code>.
                Clear (or pick the orchestrator default) to fall back to{" "}
                <code className="text-ink-200">cfg.DefaultEngine</code>.
              </p>
            </div>
          </div>

          {q.isLoading && <p className="text-sm text-ink-400">loading…</p>}
          {q.error && (
            <p className="text-sm text-rose-300">
              Failed to load: {(q.error as Error).message}
            </p>
          )}

          {!q.isLoading && !q.error && (
            <>
              <div className="mb-4 text-xs">
                <span className="text-ink-400">Current source: </span>
                <span
                  className={
                    isOverride ? "text-emerald-300" : "text-ink-300"
                  }
                >
                  {isOverride
                    ? `admin_override (engine=${current?.default_engine})`
                    : "cfg_default (no admin override set)"}
                </span>
                {isOverride && current?.updated_at && (
                  <span className="text-ink-500">
                    {" · "}set {new Date(current.updated_at).toLocaleString()}
                    {current.updated_by_uid && ` by ${current.updated_by_uid}`}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                {(["codex", "claude"] as const).map((opt) => (
                  <label
                    key={opt}
                    className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                      activeChoice === opt
                        ? "border-accent bg-accent/10 text-ink-50"
                        : "border-ink-700 bg-ink-900/20 text-ink-300 hover:border-ink-600"
                    }`}
                  >
                    <input
                      type="radio"
                      name="engine"
                      value={opt}
                      checked={activeChoice === opt}
                      onChange={() => setSelected(opt)}
                      className="sr-only"
                    />
                    <span className="font-mono text-sm">{opt}</span>
                  </label>
                ))}

                <button
                  type="button"
                  disabled={!dirty || mut.isPending}
                  onClick={() => mut.mutate(activeChoice)}
                  className="ml-auto inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-2 text-sm font-medium text-ink-950 hover:bg-accent-glow transition-colors disabled:bg-ink-700 disabled:text-ink-400 disabled:cursor-not-allowed"
                >
                  {mut.isPending
                    ? "Saving…"
                    : dirty
                      ? "Save override"
                      : isOverride
                        ? "No change"
                        : "Set override"}
                </button>
              </div>

              <p className="text-xs text-ink-500 mt-4">
                Note: claude path through V2 sub-prompts has not been fully
                validated end-to-end. First run on claude may surface
                schema-strictness or per-stage-model-config drift; check
                the trace.
              </p>
            </>
          )}
        </section>

        <EngineAuthSection />

        <section data-testid="deployed-versions" className="rounded-md border border-ink-800 bg-ink-900/40 p-6 mt-6">
          <div className="mb-4">
            <h2 className="text-base font-medium text-ink-50">Deployed versions</h2>
            <p className="text-xs text-ink-400 mt-1">
              The git commit each part was built from ("dev" for a local build).
            </p>
          </div>
          <div className="space-y-2 text-sm font-mono">
            <div className="flex items-baseline gap-4">
              <span className="w-24 text-ink-300">admin app</span>
              <span data-testid="admin-commit" className="text-ink-100">{import.meta.env.VITE_BUILD_COMMIT}</span>
            </div>
            <div className="flex items-baseline gap-4">
              <span className="w-24 text-ink-300">service</span>
              <span data-testid="service-commit" className="text-ink-100">{serviceCommit}</span>
            </div>
          </div>
        </section>

        <section className="rounded-md border border-ink-800 bg-ink-900/40 p-6 mt-6">
          <div className="mb-4">
            <h2 className="text-base font-medium text-ink-50">CLI versions</h2>
            <p className="text-xs text-ink-400 mt-1">
              Versions of <code className="text-ink-200">codex</code> and{" "}
              <code className="text-ink-200">claude-code</code> baked into the
              current Cloud Run image, compared against the latest published
              on npm. When an update is available, redeploy: the deploy
              installs npm's latest.
            </p>
          </div>

          {versionsQ.isLoading && <p className="text-sm text-ink-400">loading…</p>}
          {versionsQ.error && (
            <p className="text-sm text-rose-300">
              Failed to load: {(versionsQ.error as Error).message}
            </p>
          )}

          {versionsQ.data && (
            <div className="space-y-3">
              <CLIVersionRow label="codex" pkg="@openai/codex" pair={versionsQ.data.codex} />
              <CLIVersionRow label="claude" pkg="@anthropic-ai/claude-code" pair={versionsQ.data.claude} />
            </div>
          )}
        </section>

        <BrowserAlertsSetting />

      </main>
    </div>
  );
}

// EngineAuthSection — per-engine authentication status + in-orchestrator
// sign-in. The orchestrator performs the OAuth code-exchange itself; the admin
// only authenticates in a browser and pastes back a CODE (never a token). Rows
// are driven entirely by /capabilities (no hardcoded engine list).
function EngineAuthSection() {
  const queryClient = useQueryClient();
  const capsQ = useQuery({
    queryKey: ["admin", "engine-auth", "capabilities"],
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/admin/settings/engine-auth/capabilities",
        { params: { header: { "X-Requested-With": "xhr" } } },
      );
      if (error) throw error;
      return data as EngineAuthCapability[];
    },
  });

  // Which engine's inline sign-in panel is open, plus its minted session + the
  // code the admin pastes back.
  const [openEngine, setOpenEngine] = useState<EngineId | null>(null);
  const [session, setSession] = useState<EngineAuthStartResponse | null>(null);
  const [code, setCode] = useState("");

  const startMut = useMutation({
    mutationFn: async (engine: EngineId) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/admin/settings/engine-auth/{engine}/start",
        { params: { header: { "X-Requested-With": "xhr" }, path: { engine } } },
      );
      if (error) throw error;
      return data as EngineAuthStartResponse;
    },
    onSuccess: (data) => setSession(data),
    onError: (err) => pushToast(`Couldn't start sign-in: ${(err as Error).message}`, "error"),
  });

  const completeMut = useMutation({
    mutationFn: async (vars: { engine: EngineId; session_id: string; code: string }) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/admin/settings/engine-auth/{engine}/complete",
        {
          params: { header: { "X-Requested-With": "xhr" }, path: { engine: vars.engine } },
          body: { session_id: vars.session_id, code: vars.code },
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      const exp = data?.expires_at ? ` · expires ${new Date(data.expires_at).toLocaleDateString()}` : "";
      pushToast(`Signed in${exp}`);
      setOpenEngine(null);
      setSession(null);
      setCode("");
      queryClient.invalidateQueries({ queryKey: ["admin", "engine-auth", "capabilities"] });
    },
    onError: () => pushToast("Code rejected — restart the sign-in", "error"),
  });

  function beginSignIn(engine: EngineId) {
    setOpenEngine(engine);
    setSession(null);
    setCode("");
    startMut.mutate(engine);
  }

  function cancel() {
    setOpenEngine(null);
    setSession(null);
    setCode("");
  }

  // Device-code flow (codex): the session carries verification_url + user_code
  // instead of authorize_url, and there is nothing to paste — we poll /status
  // until the CLI subprocess exits. Claude is synchronous (paste → complete) and
  // never polls.
  const isDeviceFlow = !!session?.verification_url;
  const statusQ = useQuery({
    queryKey: ["admin", "engine-auth", "status", openEngine, session?.session_id],
    enabled: !!openEngine && !!session?.session_id && isDeviceFlow,
    refetchInterval: (q) => {
      const st = (q.state.data as EngineAuthStatusResponse | undefined)?.status;
      return st && st !== "pending" ? false : 3000;
    },
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/admin/settings/engine-auth/{engine}/status",
        {
          params: {
            header: { "X-Requested-With": "xhr" },
            path: { engine: openEngine! },
            query: { session_id: session!.session_id },
          },
        },
      );
      if (error) throw error;
      return data as EngineAuthStatusResponse;
    },
  });

  // React to terminal device-flow states: close the panel + refresh the pill.
  useEffect(() => {
    const st = statusQ.data?.status;
    if (!st || st === "pending") return;
    if (st === "authorized") {
      const exp = statusQ.data?.expires_at
        ? ` · expires ${new Date(statusQ.data.expires_at).toLocaleDateString()}`
        : "";
      pushToast(`Signed in${exp}`);
      queryClient.invalidateQueries({ queryKey: ["admin", "engine-auth", "capabilities"] });
    } else if (st === "failed") {
      pushToast(`Sign-in failed: ${statusQ.data?.error || "the codex CLI exited"}`, "error");
    } else if (st === "expired") {
      pushToast("Sign-in expired — start again", "error");
    }
    cancel();
  }, [statusQ.data?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="rounded-md border border-ink-800 bg-ink-900/40 p-6 mt-6">
      <div className="mb-4">
        <h2 className="text-base font-medium text-ink-50">Engine authentication</h2>
        <p className="text-xs text-ink-400 mt-1">
          Sign an engine in directly from here — the orchestrator runs the OAuth
          flow and stores the token. You authenticate in your browser; depending
          on the engine you either paste back a short code or enter a device code
          (never a token). Takes effect on the running instance immediately.
        </p>
      </div>

      {capsQ.isLoading && <p className="text-sm text-ink-400">loading…</p>}
      {capsQ.error && (
        <p className="text-sm text-rose-300">
          Failed to load: {(capsQ.error as Error).message}
        </p>
      )}

      {capsQ.data && (
        <div className="space-y-2">
          {capsQ.data.map((cap) => (
            <div key={cap.engine}>
              <EngineAuthRow
                cap={cap}
                busy={startMut.isPending && openEngine === cap.engine}
                onSignIn={() => beginSignIn(cap.engine as EngineId)}
              />
              <EngineUsageBlock engine={cap.engine as EngineId} />
              {openEngine === cap.engine && (
                <SignInPanel
                  engine={cap.engine}
                  starting={startMut.isPending}
                  session={session}
                  deviceStatus={statusQ.data?.status}
                  deviceError={statusQ.data?.error}
                  code={code}
                  setCode={setCode}
                  submitting={completeMut.isPending}
                  onSubmit={() =>
                    session &&
                    completeMut.mutate({
                      engine: cap.engine as EngineId,
                      session_id: session.session_id,
                      code,
                    })
                  }
                  onCancel={cancel}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// daysUntil returns whole days from now to an RFC3339 timestamp (negative if
// past). Used for the expiry warning threshold.
function daysUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.floor(ms / (24 * 60 * 60 * 1000));
}

// expiryLabel renders the exact expiry date AND time + a relative-days hint.
function expiryLabel(iso: string): string {
  const days = daysUntil(iso);
  return `${new Date(iso).toLocaleString()} (${days}d)`;
}

// resetLabel renders a friendly reset time for a rate-limit window: a short
// relative hint ("in 42m" / "in 3h") when the reset is within a day, else the
// wall-clock HH:MM. A reset already in the past reads "now".
function resetLabel(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return iso;
  if (ms <= 0) return "now";
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `in ${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `in ${hours}h`;
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// relativeAge renders how long ago a timestamp (ms) was, for the "last refresh"
// line: "just now" / "Ns ago" / "Nm ago" / "Nh ago".
function relativeAge(updatedAtMs: number, nowMs: number): string {
  const secs = Math.max(0, Math.floor((nowMs - updatedAtMs) / 1000));
  if (secs < 5) return "just now";
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ago`;
}

// usageSeverityClass maps a used_percent (0..100) to a Tailwind fill color:
// healthy <75 emerald, warming <90 amber, hot else rose.
function usageSeverityClass(pct: number): string {
  if (pct < 75) return "bg-emerald-400";
  if (pct < 90) return "bg-amber-400";
  return "bg-rose-400";
}

// EngineUsageBlock — per-engine live usage display. Owns its OWN usage query
// (one per engine), the Refresh button, the "last refresh: Xs ago" age, and the
// UsageBars. The query auto-fetches once on mount (staleTime 60s, no polling).
// On a settled refetch it invalidates /capabilities so the row pill re-syncs to
// any background-run-driven auth_error / rate_limited flip (and clears a
// recovered one).
export function EngineUsageBlock({ engine }: { engine: EngineId }) {
  const queryClient = useQueryClient();
  const usageQ = useQuery({
    queryKey: ["admin", "engine-auth", "usage", engine],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/admin/settings/engine-auth/{engine}/usage",
        { params: { header: { "X-Requested-With": "xhr" }, path: { engine } } },
      );
      if (error) throw error;
      return data as EngineUsage;
    },
  });

  // 1s ticker so "last refresh: Xs ago" advances live without re-fetching.
  // Only run while there's actually data to age (dataUpdatedAt > 0) — before the
  // first probe lands there's no age line, so a timer would re-render for
  // nothing. dataUpdatedAt is in the deps so the interval starts once data
  // arrives (and the cleanup re-runs if a refetch bumps the timestamp).
  const dataUpdatedAt = usageQ.dataUpdatedAt;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!dataUpdatedAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [dataUpdatedAt]);

  const [refreshing, setRefreshing] = useState(false);
  async function onRefresh() {
    setRefreshing(true);
    try {
      const { data } = await usageQ.refetch();
      const st = data?.status;
      if (st === "auth_expired") {
        pushToast(`Token rejected — re-sign-in ${engine}`, "error");
      } else if (st === "rate_limited") {
        pushToast(`${engine}: rate limit reached`);
      } else if (st === "stale") {
        pushToast(`${engine} usage throttled — showing last reading`);
      } else if (st === "error") {
        pushToast(`Couldn't fetch ${engine} usage`, "error");
      }
    } catch {
      // Rare true transport failure (the endpoint normally returns 200 with a
      // discriminated status). onClick doesn't await, so swallow it here so no
      // unhandled rejection escapes; surface a toast instead.
      pushToast(`Couldn't refresh ${engine} usage`, "error");
    } finally {
      // On settle, re-sync the row pill to any background-run flip (recovered
      // or newly rate-limited/rejected). Runs whether or not the probe was ok.
      queryClient.invalidateQueries({ queryKey: ["admin", "engine-auth", "capabilities"] });
      setRefreshing(false);
    }
  }

  const usage = usageQ.data;
  // "API refresh" dates the data to when it was actually fetched FROM ANTHROPIC —
  // the response's fetched_at (live time for ok/rate_limited; the cached data's
  // original fetch time for stale). NOT dataUpdatedAt, which is when the browser
  // last received a response and would read "just now" even off a cached/throttled
  // serve. Only shown for statuses where fetched_at reflects a real provider fetch
  // (ok/rate_limited/stale); error/unavailable carry no real fetch.
  const apiFetchedAt =
    usage && (usage.status === "ok" || usage.status === "rate_limited" || usage.status === "stale") && usage.fetched_at
      ? Date.parse(usage.fetched_at)
      : NaN;
  const ageLine = Number.isFinite(apiFetchedAt)
    ? `API refresh: ${relativeAge(apiFetchedAt, now)}`
    : "";

  return (
    <div className="ml-5 mt-1.5 mb-1 text-xs">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <UsageBars usage={usage} loading={usageQ.isLoading} />
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            type="button"
            disabled={refreshing || usageQ.isFetching}
            onClick={onRefresh}
            className="rounded-md border border-ink-700 bg-ink-900/30 px-2.5 py-1 text-xs text-ink-200 hover:border-ink-600 transition-colors disabled:cursor-not-allowed disabled:text-ink-500"
          >
            {refreshing || usageQ.isFetching ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-ink-700 border-t-accent" aria-hidden />
                Refreshing
              </span>
            ) : (
              "Refresh"
            )}
          </button>
          {ageLine && <span className="text-ink-500">{ageLine}</span>}
        </div>
      </div>
    </div>
  );
}

// UsageBars — two thin meter rows (5-hour, weekly) driven by the usage probe.
// When usage is unavailable/error or hasn't loaded yet, a single muted line
// stands in for the bars. When "stale" (the provider 429'd the probe), the last
// cached bars are shown DIMMED with a "throttled" badge + a note dating the
// reading — deliberately NOT the amber "rate limit reached" styling, which means
// the engine's own quota (a different thing).
function UsageBars({ usage, loading }: { usage?: EngineUsage; loading: boolean }) {
  if (!usage) {
    return <p className="text-ink-500">{loading ? "loading…" : "— usage unavailable"}</p>;
  }
  if (usage.status === "unavailable" || usage.status === "error") {
    return <p className="text-ink-500">— usage unavailable</p>;
  }
  if (usage.status === "stale") {
    const retry = staleRetryHint(usage.retry_after);
    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="rounded border border-ink-700 bg-ink-800/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-ink-400">
            throttled
          </span>
          <span className="text-ink-500">
            usage temporarily unavailable (provider throttled) — last reading{" "}
            {usage.fetched_at ? resetLabelAge(usage.fetched_at) : "unknown"}
            {retry ? `, retry ${retry}` : ""}
          </span>
        </div>
        <div className="space-y-1.5 opacity-50">
          <UsageBar label="5-hour" win={usage.five_hour} />
          <UsageBar label="weekly" win={usage.weekly} />
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <UsageBar label="5-hour" win={usage.five_hour} />
      <UsageBar label="weekly" win={usage.weekly} />
    </div>
  );
}

// staleRetryHint renders a stale probe's retry_after (seconds) as a short hint
// ("in 5m" / "in 42s"), or "" when absent.
function staleRetryHint(secs?: number): string {
  if (secs === undefined || secs === null || secs <= 0) return "";
  if (secs < 60) return `in ${secs}s`;
  return `in ${Math.round(secs / 60)}m`;
}

// resetLabelAge renders how long ago an RFC3339 timestamp was, for the stale
// "last reading" note: "just now" / "Ns ago" / "Nm ago" / "Nh ago".
function resetLabelAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  const secs = Math.max(0, Math.floor(ms / 1000));
  if (secs < 5) return "just now";
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

function UsageBar({ label, win }: { label: string; win?: EngineUsageWindow }) {
  // An omitted window (API omits the key when the provider reports no such
  // window) renders "No data available" — owner requirement (b), replacing the
  // old "—".
  if (!win) {
    return (
      <div className="flex items-center gap-2">
        <span className="w-14 text-ink-400">{label}</span>
        <span className="text-ink-500">No data available</span>
      </div>
    );
  }
  const pct = Math.max(0, Math.min(100, win.used_percent));
  return (
    <div className="flex items-center gap-2">
      <span className="w-14 text-ink-400">{label}</span>
      <span className="relative h-1.5 flex-1 max-w-[12rem] overflow-hidden rounded-full bg-ink-800">
        <span
          className={`absolute inset-y-0 left-0 rounded-full ${usageSeverityClass(pct)}`}
          style={{ width: `${pct}%` }}
        />
      </span>
      <span className="w-9 text-right tabular-nums text-ink-200">{Math.round(pct)}%</span>
      {/* Only render the reset label when resets_at is present AND parses to a
          real post-epoch time. The API already blanks epoch-0 + omits absent
          windows, but guard anyway so a stray "1970"/epoch-0 reset can never
          render "resets now" if one somehow reaches the client. */}
      {win.resets_at && new Date(win.resets_at).getTime() > 0 && (
        <span
          className="text-ink-500 cursor-help"
          title={new Date(win.resets_at).toLocaleString()}
        >
          resets {resetLabel(win.resets_at)}
        </span>
      )}
    </div>
  );
}

function EngineAuthRow({
  cap, busy, onSignIn,
}: {
  cap: EngineAuthCapability;
  busy: boolean;
  onSignIn: () => void;
}) {
  const days = cap.expires_at ? daysUntil(cap.expires_at) : null;
  // Auto-rotating tokens (codex, ~10d) roll on their own, so a <30d countdown
  // would be permanently lit — no "expiring soon" warning for them.
  const expiringSoon = cap.authenticated && !cap.auto_rotates && days !== null && days < 30;

  // Dot + status text by (authenticated, source). signed_in = we minted it
  // (green, trustworthy). deploy_credential = present via the deploy mount, not
  // through this flow (amber — provenance unverified; "sign in" to be sure).
  // none / expired = rose.
  let dotClass = "bg-rose-400";
  let textClass = "text-rose-300";
  let statusText: ReactNode = "not authenticated";

  // Precedence (highest first): auth_error (the live token was rejected, 401)
  // dominates everything — the credential is dead, re-sign-in. Then rate_limited
  // (still authenticated, just throttled). Then the existing signed-in / expiry
  // logic. auth_error/rate_limited are surfaced by /capabilities after a usage
  // probe (manual Refresh or a background run).
  if (cap.auth_error) {
    dotClass = "bg-rose-400";
    textClass = "text-rose-300";
    statusText = "token rejected — re-sign-in";
  } else if (cap.rate_limited) {
    dotClass = "bg-amber-400";
    textClass = "text-amber-300";
    statusText = cap.rate_limit_resets_at
      ? `signed in · rate limit reached · resets ${resetLabel(cap.rate_limit_resets_at)}`
      : "signed in · rate limit reached";
  } else if (cap.refresh_pending) {
    // Auto-rotating engine (codex): the access token expired but the refresh
    // chain is intact — it self-heals on the next run/refresh. Benign amber, NOT
    // the rose "token rejected" state; no re-sign-in is needed.
    dotClass = "bg-amber-400";
    textClass = "text-amber-300";
    statusText = `expires ${expiryLabel(cap.expires_at!)} — refreshes automatically on next run, no re-sign-in needed`;
  } else if (!cap.authenticated) {
    if (cap.source === "deploy_credential" && cap.expires_at) {
      statusText = `deploy credential expired · ${expiryLabel(cap.expires_at)}`;
    } else {
      statusText = "not authenticated";
    }
  } else if (cap.source === "signed_in") {
    if (expiringSoon) {
      dotClass = "bg-amber-400";
      textClass = "text-amber-300";
      statusText = `signed in · expires in ${days}d — re-sign-in soon · ${new Date(cap.expires_at!).toLocaleString()}`;
    } else {
      dotClass = "bg-emerald-400";
      textClass = "text-ink-300";
      const rot = cap.auto_rotates ? " · auto-refreshes" : "";
      statusText = cap.expires_at
        ? `signed in · expires ${expiryLabel(cap.expires_at)}${rot}`
        : "signed in";
    }
  } else {
    // deploy_credential, authenticated. Amber for login-capable engines (a
    // leftover deploy credential, not a verified sign-in); neutral for codex
    // (no flow exists, so the deploy credential IS the expected state).
    dotClass = cap.login_supported ? "bg-amber-400" : "bg-emerald-400";
    textClass = cap.login_supported ? "text-amber-300" : "text-ink-300";
    const base = cap.login_supported ? "deploy credential" : "authenticated";
    statusText = cap.expires_at
      ? `${base} · expires ${expiryLabel(cap.expires_at)}`
      : `${base} present`;
  }

  return (
    <div className="flex items-center gap-3 text-sm">
      <span className={`inline-block h-2 w-2 rounded-full ${dotClass}`} aria-hidden />
      <span className="w-16 font-mono text-ink-200">{cap.engine}</span>
      <span className={textClass} title={cap.expires_at ? `expires ${new Date(cap.expires_at).toISOString()}` : undefined}>
        {statusText}
      </span>
      <div className="ml-auto">
        {cap.login_supported ? (
          <button
            type="button"
            disabled={busy}
            onClick={onSignIn}
            className="rounded-md border border-ink-700 bg-ink-900/30 px-3 py-1.5 text-xs text-ink-100 hover:border-ink-600 transition-colors disabled:cursor-not-allowed disabled:text-ink-500"
          >
            {cap.source === "signed_in" ? "Re-sign-in" : "Sign in"}
          </button>
        ) : (
          <span
            className="inline-block rounded-md border border-ink-800 px-3 py-1.5 text-xs text-ink-500"
            title="In-orchestrator sign-in for this engine is not available yet"
          >
            Coming soon
          </span>
        )}
      </div>
    </div>
  );
}

function SignInPanel({
  engine, starting, session, deviceStatus, deviceError, code, setCode, submitting, onSubmit, onCancel,
}: {
  engine: string;
  starting: boolean;
  session: EngineAuthStartResponse | null;
  deviceStatus?: string;
  deviceError?: string;
  code: string;
  setCode: (v: string) => void;
  submitting: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  // Device-code flow (codex): the session carries a verification_url + user_code
  // to display — nothing is pasted back; the parent polls /status. The paste
  // flow (claude) carries an authorize_url + a code field.
  const isDevice = !!session?.verification_url;
  return (
    <div className="mt-2 mb-3 ml-5 rounded-md border border-ink-700 bg-ink-950/40 p-4 text-sm">
      <div className="mb-3 font-medium text-ink-100">Sign in to {engine}</div>
      {starting && <p className="text-ink-400">preparing sign-in…</p>}

      {session && isDevice && (
        <ol className="space-y-3">
          <li>
            <span className="text-ink-300">1. Open this link and sign in to your account:</span>
            <div className="mt-1">
              <a
                href={session.verification_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:text-accent-glow underline break-all font-mono text-xs"
              >
                {session.verification_url} ↗
              </a>
            </div>
          </li>
          <li>
            <span className="text-ink-300">2. Enter this one-time code:</span>
            <div className="mt-1">
              <code className="inline-block rounded-md border border-ink-700 bg-ink-950 px-3 py-1.5 font-mono text-base tracking-widest text-ink-50">
                {session.user_code}
              </code>
            </div>
          </li>
          <li className="border-t border-ink-800 pt-3">
            {deviceStatus === "failed" ? (
              <span className="text-rose-300">
                Sign-in failed{deviceError ? `: ${deviceError}` : ""}. Cancel and try again.
              </span>
            ) : deviceStatus === "expired" ? (
              <span className="text-rose-300">The code expired. Cancel and start again.</span>
            ) : (
              <span className="inline-flex items-center gap-2 text-ink-400">
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-ink-700 border-t-accent" aria-hidden />
                Waiting for you to authorize in the browser — this updates automatically.
              </span>
            )}
          </li>
        </ol>
      )}

      {session && !isDevice && (
        <ol className="space-y-3">
          <li>
            <span className="text-ink-300">1. Open to authenticate:</span>{" "}
            <a
              href={session.authorize_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:text-accent-glow underline break-all"
            >
              open ↗
            </a>
          </li>
          <li>
            <span className="text-ink-300">2. Paste the code shown after sign-in:</span>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="paste code"
                className="flex-1 min-w-[16rem] rounded-md border border-ink-700 bg-ink-900/60 px-3 py-1.5 font-mono text-xs text-ink-100 focus:border-accent focus:outline-none"
              />
              <button
                type="button"
                disabled={!code.trim() || submitting}
                onClick={onSubmit}
                className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-ink-950 hover:bg-accent-glow transition-colors disabled:bg-ink-700 disabled:text-ink-400 disabled:cursor-not-allowed"
              >
                {submitting ? "validating…" : "Submit"}
              </button>
            </div>
          </li>
        </ol>
      )}

      <button
        type="button"
        onClick={onCancel}
        className="mt-3 text-xs text-ink-400 hover:text-ink-200"
      >
        Cancel
      </button>
    </div>
  );
}

function CLIVersionRow({
  label, pkg, pair,
}: { label: string; pkg: string; pair: CLIVersionStatusPair }) {
  const installed = pair.installed || "unknown";
  const latest = pair.latest || (pair.latest_error ? "couldn't check" : "—");
  const updateAvail = pair.update_available;
  return (
    <div className="flex items-baseline gap-4 text-sm font-mono">
      <span className="w-16 text-ink-300">{label}</span>
      <span className="text-ink-100">{installed}</span>
      <span className="text-ink-500">/</span>
      <span className={pair.latest_error ? "text-amber-300" : "text-ink-300"}>
        latest {latest}
      </span>
      <span className="text-ink-500 text-xs">({pkg})</span>
      {updateAvail && (
        <span
          className="ml-auto inline-flex items-center gap-1 rounded-md border border-amber-700/50 bg-amber-950/30 px-2 py-0.5 text-xs text-amber-200"
          title="Redeploy to install it"
        >
          update available
        </span>
      )}
      {!updateAvail && pair.installed && pair.latest && (
        <span className="ml-auto text-xs text-emerald-300">up to date</span>
      )}
      {pair.latest_error && (
        <span className="ml-auto text-xs text-amber-300" title={pair.latest_error}>
          registry unreachable
        </span>
      )}
    </div>
  );
}
